import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { useNavigate } from 'react-router-dom';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import { formatDate } from '../../helpers/UtilsFunctions';
import { format } from 'date-fns';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import emptyDocIcon from '../../assets/empty_doc.svg';
import { DataTable } from '../Common';
import TaskDetailSidePanel from './TaskDetailSidePanel';
import './Projects.css';

// Helper function to handle profile picture URL (same as DashboardLayout)
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

const AllTasks = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isLoadingEnums, setIsLoadingEnums] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // Default to 'active' tab
  const [pagination, setPagination] = useState(null);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [currentUserId, setCurrentUserId] = useState(null);

  // Custom theme for Material-UI components to match current styling
  const customTheme = createTheme({
    palette: {
      mode: mode,
      primary: {
        main: '#26C3C0',
      },
      background: {
        default: mode === 'dark' ? '#000000' : '#f8f9fa',
        paper: mode === 'dark' ? '#000000' : '#f8f9fa',
      },
      text: {
        primary: mode === 'dark' ? '#FFFFFF' : '#222222',
        secondary: mode === 'dark' ? '#666' : '#999',
      },
    },
    components: {
      MuiSelect: {
        styleOverrides: {
          root: {
            fontFamily: "'Urbanist', Arial, sans-serif",
            fontSize: '14px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? '#444' : '#ddd',
              borderRadius: '6px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#26C3C0',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#26C3C0',
            },
          },
        },
      },
             MuiInputLabel: {
         styleOverrides: {
           root: {
             color: mode === 'dark' ? '#666' : '#999',
             fontFamily: "'Urbanist', Arial, sans-serif",
             fontSize: '14px',
             '&.Mui-focused': {
               color: '#26C3C0',
             },
                           '&.MuiInputLabel-shrink': {
                color: mode === 'dark' ? '#FFE4B5' : '#222222',
                fontWeight: '600',
                fontSize: '13px',
              },
           },
         },
       },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: "'Urbanist', Arial, sans-serif",
            fontSize: '14px',
          },
        },
      },
    },
  });

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    fetchTaskEnums();
    
    // Get current user ID from localStorage
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedUserData = JSON.parse(userData);
        const userId = parsedUserData.id || parsedUserData.user_id;
        setCurrentUserId(userId);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, []);

  useEffect(() => {
    // Reset pagination when tab changes
    setPaginationState({
      pageIndex: 0,
      pageSize: 20,
    });
  }, [activeTab]);

  useEffect(() => {
    fetchAllTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, paginationState]);

  // Fetch task enums
  const fetchTaskEnums = async () => {
    setIsLoadingEnums(true);
    try {
      const response = await dataAPI.getTaskEnums();
      console.log('Task Enums Response:', response); // Debug log
      if (response.priorities && response.statuses) {
        setPriorityOptions(response.priorities);
        setStatusOptions(response.statuses);
      } else if (response.data && response.data.priorities && response.data.statuses) {
        // Alternative response structure
        setPriorityOptions(response.data.priorities);
        setStatusOptions(response.data.statuses);
      } else {
        console.log('Unexpected response structure:', response);
        // Try to get from localStorage as fallback
        const storedEnums = localStorage.getItem('taskEnums');
        if (storedEnums) {
          const parsedEnums = JSON.parse(storedEnums);
          setPriorityOptions(parsedEnums.priorities || []);
          setStatusOptions(parsedEnums.statuses || []);
        }
      }
    } catch (error) {
      console.error('Error fetching task enums:', error);
      // Try to get from localStorage as fallback
      const storedEnums = localStorage.getItem('taskEnums');
      if (storedEnums) {
        const parsedEnums = JSON.parse(storedEnums);
        setPriorityOptions(parsedEnums.priorities || []);
        setStatusOptions(parsedEnums.statuses || []);
      }
    } finally {
      setIsLoadingEnums(false);
    }
  };

  const fetchAllTasks = async () => {
    setIsLoading(true);
    setError('');
    try {
      const status = activeTab === 'active' 
        ? 'To Do, In Progress, In Review' 
        : 'Completed';
      
      const offset = paginationState.pageIndex * paginationState.pageSize;
      
      const params = {
        limit: paginationState.pageSize,
        offset: offset,
        status: status
      };
      
      const response = await dataAPI.getAllTasks(params);
      
      if (response.status === 'success') {
        setTasks(response.tasks || []);
        setPagination(response.pagination || null);
      } else {
        setError('Failed to load tasks');
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskClick = (task) => {
    navigate(`/project/${task.project_id}/task/${task.id}`, {
      state: {
        fromManageTasks: true
      }
    });
  };

  const handleSidePanelClose = () => {
    setIsSidePanelOpen(false);
    setSelectedTaskId(null);
  };

  const handlePaginationChange = (updater) => {
    setPaginationState((prev) => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      // Reset to first page if pageSize changed
      if (newState.pageSize !== prev.pageSize) {
        return {
          ...newState,
          pageIndex: 0,
        };
      }
      return newState;
    });
  };

  const handleAcceptTask = async (task, event) => {
    // Prevent row click when clicking accept button
    event.stopPropagation();
    
    try {
      setIsLoading(true);
      // Update task status to "In Progress" when accepting (allowed values: To Do, In Progress, In Review, Completed)
      const response = await dataAPI.updateTaskStatus(task.id, 'In Progress');
      
      if (response.status === 'success') {
        // Refresh the tasks list
        await fetchAllTasks();
      } else {
        setError('Failed to accept task. Please try again.');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error accepting task:', error);
      setError('Failed to accept task. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter tasks based on selected filters - Commented out for now
  // Since API returns filtered tasks, we use tasks directly
  const filteredTasks = tasks;
  // const filteredTasks = tasks.filter(task => {
  //   const matchesPriority = !selectedPriority || task.priority === selectedPriority;
  //   const matchesStatus = !selectedStatus || task.status === selectedStatus;
  //   return matchesPriority && matchesStatus;
  // });

  // Debug log to see what options we have
  console.log('Priority Options:', priorityOptions);
  console.log('Status Options:', statusOptions);

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return '#f44336'; // Red for Urgent
      case 'high':
        return '#f44336';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return '#666666';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'done':
        return '#4caf50';
      case 'in_progress':
        return '#2196f3';
      case 'to_do':
      case 'pending':
        return '#ff9800';
      default:
        return '#666666';
    }
  };

  const columns = [
    {
      accessorKey: 'title',
      header: 'Task Title',
      size: 200,
      minSize: 150,
      maxSize: 300,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: "'Urbanist', Arial, sans-serif",
          fontWeight: '500',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          lineHeight: '1.4'
        }}>
          {row.original.title}
        </span>
      ),
    },
         {
       accessorKey: 'description',
       header: 'Description',
       size: 250,
       minSize: 200,
       maxSize: 400,
       Cell: ({ row }) => {
         const description = row.original.description;
         const truncatedDescription = description?.length > 100 
           ? `${description.substring(0, 100)}...` 
           : description || 'No description';
         
         return (
           <div 
             style={{ 
               color: mode === 'dark' ? '#CCCCCC !important' : '#666666 !important',
               fontSize: '14px',
               fontFamily: "'Urbanist', Arial, sans-serif",
               wordWrap: 'break-word',
               whiteSpace: 'normal',
               lineHeight: '1.4',
               maxHeight: '60px',
               overflow: 'hidden'
             }}
             dangerouslySetInnerHTML={{ 
               __html: `<div style="color: inherit !important;">${truncatedDescription}</div>`
             }}
           />
         );
       },
     },
    {
      accessorKey: 'priority',
      header: 'Priority',
      size: 100,
      minSize: 80,
      maxSize: 120,
      Cell: ({ row }) => {
        const priority = row.original.priority;
        return (
          <span style={{ 
            backgroundColor: getPriorityColor(priority),
            color: '#FFFFFF',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '12px',
            fontFamily: "'Urbanist', Arial, sans-serif",
            fontWeight: '600',
            textTransform: 'capitalize'
          }}>
            {priority || 'Medium'}
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      minSize: 100,
      maxSize: 140,
      Cell: ({ row }) => {
        const status = row.original.status;
        return (
          <span style={{ 
            backgroundColor: getStatusColor(status),
            color: '#FFFFFF',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '12px',
            fontFamily: "'Urbanist', Arial, sans-serif",
            fontWeight: '600',
            textTransform: 'capitalize'
          }}>
            {status?.replace('_', ' ') || 'To Do'}
          </span>
        );
      },
    },
    {
      accessorKey: 'project_name',
      header: 'Project',
      size: 150,
      minSize: 120,
      maxSize: 200,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: "'Urbanist', Arial, sans-serif",
          wordWrap: 'break-word',
          whiteSpace: 'normal'
        }}>
          {row.original.project_name || 'N/A'}
        </span>
      ),
    },
    {
      accessorKey: 'due_date',
      header: 'Due Date',
      size: 120,
      minSize: 100,
      maxSize: 150,
      Cell: ({ row }) => {
        const task = row.original;
        const dueDateText = task.due_date ? 
          formatDate(task.due_date) : 
          'Not set';
        
        return (
          <span style={{ 
            color: mode === 'dark' ? '#FFFFFF' : '#333333',
            fontSize: '14px',
            fontFamily: "'Urbanist', Arial, sans-serif",
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            lineHeight: '1.4'
          }}>
            {dueDateText}
          </span>
        );
      },
    },
    {
      accessorKey: 'assigned_to_full_name',
      header: 'Assigned To',
      size: 150,
      minSize: 120,
      maxSize: 180,
      Cell: ({ row }) => (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {row.original.assigned_to_profile_pic ? (
            <img
              src={getProfilePicUrl(row.original.assigned_to_profile_pic)}
              alt="Profile"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                // Replace the image with a fallback avatar when it fails to load
                const fallbackDiv = document.createElement('div');
                fallbackDiv.style.cssText = `
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  background-color: #26C3C0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #FFFFFF;
                  font-size: 12px;
                  font-weight: 600;
                `;
                fallbackDiv.textContent = (row.original.assigned_to_full_name || 'U').charAt(0).toUpperCase();
                e.target.parentNode.replaceChild(fallbackDiv, e.target);
              }}
            />
          ) : (
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#26C3C0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {(row.original.assigned_to_full_name || 'U').charAt(0).toUpperCase()}
            </div>
          )}
          <span style={{ 
            color: mode === 'dark' ? '#FFFFFF' : '#333333',
            fontSize: '14px',
            fontFamily: "'Urbanist', Arial, sans-serif"
          }}>
            {row.original.assigned_to_full_name || 'Unassigned'}
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 140,
      minSize: 120,
      maxSize: 180,
      enableSorting: false,
      Cell: ({ row }) => {
        const task = row.original;
        const taskStatus = (task.status || '').trim();
        const taskStatusLower = taskStatus.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ');
        
        // Check for "To Do" status (handles: "To Do", "to do", "todo", "to_do", "To Do ", etc.)
        // Normalize by removing underscores and extra spaces, then compare
        const normalizedStatus = taskStatusLower.replace(/\s+/g, ' ').trim();
        const isTodoStatus = normalizedStatus === 'to do' || 
                            normalizedStatus === 'todo' ||
                            taskStatus === 'To Do' ||
                            taskStatus === 'to do' ||
                            taskStatus === 'todo' ||
                            taskStatus === 'to_do';
        
        // Check if current user is assigned to this task - check multiple possible field names
        const assignedToId = task.assigned_to || 
                            task.assigned_to_user_id || 
                            task.assigned_user_id || 
                            task.assigned_to_id ||
                            task.assigned_user?.id;
        
        // Compare user IDs (handle both string and number types)
        const isAssignedToCurrentUser = currentUserId && assignedToId && 
          (String(assignedToId) === String(currentUserId) || 
           Number(assignedToId) === Number(currentUserId));
        
        
        // Show Accept button only if task is "To Do" AND assigned to current user
        const showAcceptButton = isTodoStatus && isAssignedToCurrentUser;
        
        // Show Accepted label if task status is not "To Do"
        const showAcceptedLabel = !isTodoStatus;
        
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {showAcceptButton && (
              <button
                onClick={(e) => handleAcceptTask(task, e)}
                disabled={isLoading}
                style={{
                  background: '#26C3C0',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  transition: 'all 0.2s ease',
                  opacity: isLoading ? 0.6 : 1,
                  boxShadow: '0 2px 4px rgba(38, 195, 192, 0.2)',
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = '#22B0AD';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(38, 195, 192, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = '#26C3C0';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(38, 195, 192, 0.2)';
                  }
                }}
              >
                Accept
              </button>
            )}
            {showAcceptedLabel && (
              <span
                style={{
                  color: '#4caf50',
                  fontSize: '14px',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  fontWeight: '500',
                }}
              >
                Accepted
              </span>
            )}
          </div>
        );
      },
    }
  ];

  if (isLoading && !isSidePanelOpen) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          fontSize: '18px',
          color: mode === 'dark' ? '#FFFFFF' : '#222222',
          fontFamily: "'Urbanist', Arial, sans-serif"
        }}>
          Loading tasks...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '18px',
          color: '#f44336',
          marginBottom: '16px',
          fontFamily: "'Urbanist', Arial, sans-serif"
        }}>
          {error}
        </div>
        <button 
          onClick={fetchAllTasks}
          style={{
            background: '#26C3C0',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            fontFamily: "'Urbanist', Arial, sans-serif",
            transition: 'background-color 0.2s ease'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty Tasks State
  if (!isLoading && !error && tasks.length === 0) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        {/* Empty Tasks Icon */}
        <div style={{
          marginBottom: '24px'
        }}>
          <img 
            src={emptyDocIcon} 
            alt="No Tasks" 
            style={{
              width: '71px',
              height: '59px',
              filter: mode === 'dark' ? 'brightness(1)' : 'brightness(0.8)'
            }}
          />
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '20px',
          fontWeight: '700',
          color: mode === 'dark' ? '#FFFFFF' : '#222222',
          margin: '0 0 16px 0',
          fontFamily: "'Urbanist', Arial, sans-serif",
          lineHeight: '1.2'
        }}>
          {activeTab === 'active' ? 'No Active Tasks' : 'No Completed Tasks'}
        </h1>

        {/* Description */}
        <p style={{
          fontSize: '16px',
          color: mode === 'dark' ? '#FFFFFF' : '#666666',
          margin: '0 0 32px 0',
          maxWidth: '600px',
          lineHeight: '1.5',
          fontFamily: "'Urbanist', Arial, sans-serif"
        }}>
          {activeTab === 'active' 
            ? 'No active tasks found. Active tasks include tasks with status: To Do, In Progress, or In Review.'
            : 'No completed tasks found. Completed tasks will appear here once they are marked as complete.'}
        </p>
      </div>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <div
        className="all-tasks-page"
        style={{
          padding: '24px',
          backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
          minHeight: '100vh'
        }}
      >
        {/* Header with Tabs */}
        <div
          className="all-tasks-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}
        >
          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '0',
            borderBottom: `2px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
          }}>
            <button
              onClick={() => setActiveTab('active')}
              style={{
                padding: '12px 24px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                color: activeTab === 'active' ? '#26C3C0' : (mode === 'dark' ? '#999' : '#666'),
                borderBottom: activeTab === 'active' ? '2px solid #26C3C0' : '2px solid transparent',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'all 0.2s ease',
                marginBottom: '-2px'
              }}
            >
              Active
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              style={{
                padding: '12px 24px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                color: activeTab === 'completed' ? '#26C3C0' : (mode === 'dark' ? '#999' : '#666'),
                borderBottom: activeTab === 'completed' ? '2px solid #26C3C0' : '2px solid transparent',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'all 0.2s ease',
                marginBottom: '-2px'
              }}
            >
              Completed
            </button>
          </div>

          {/* Filter Dropdowns - Commented out for now */}
          {/* <div style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
          }}>
            {/* Priority Filter */}
            {/* <FormControl 
              size="small" 
              style={{ 
                minWidth: '120px',
                backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
                borderRadius: '6px'
              }}
            >
              <InputLabel 
                style={{ 
                  color: mode === 'dark' ? '#666' : '#999',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}
              >
                Priority
              </InputLabel>
              <Select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                label="Priority"
                style={{
                  color: mode === 'dark' ? '#FFFFFF' : '#222222',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}
                
              >
                <MenuItem value="">All Priorities</MenuItem>
                {priorityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status Filter */}
            {/* <FormControl 
              size="small" 
              style={{ 
                minWidth: '120px',
                backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
                borderRadius: '6px'
              }}
            >
              <InputLabel 
                style={{ 
                  color: mode === 'dark' ? '#666' : '#999',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}
              >
                Status
              </InputLabel>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                label="Status"
                style={{
                  color: mode === 'dark' ? '#FFFFFF' : '#222222',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div> */}
        </div>

        {/* Tasks Table */}
        <div className="projects-list-wrapper">
          <DataTable
            data={filteredTasks}
            columns={columns}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search tasks..."
            onRowClick={handleTaskClick}
            loading={isLoading && !isSidePanelOpen}
            emptyMessage="No tasks found"
            enableRowSelection={false}
            enableSorting={true}
            enableGlobalFilter={true}
            enableColumnFilters={false}
            enablePagination={true}
            manualPagination={true}
            rowCount={pagination?.total_tasks || 0}
            onPaginationChange={handlePaginationChange}
            pagination={paginationState}
            pageSize={paginationState.pageSize}
            pageSizeOptions={[5, 10, 20, 25, 50]}
            hover={true}
            striped={true}
            border={true}
            shadow={false}
            borderRadius="0"
            showHorizontalScroll={true}
            customStyles={{
              backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
              border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
              boxShadow: 'none', // Remove any shadow
              borderRadius: '0', // Remove rounded corners
              '& .MuiTableRow-root': {
                borderRadius: '0 !important',
              },
              '& .MuiTableCell-root': {
                borderRadius: '0 !important',
              },
              '& .MuiTableRow-root:last-child': {
                borderRadius: '0 !important',
                '& .MuiTableCell-root': {
                  borderRadius: '0 !important',
                }
              }
            }}
          />
        </div>

        {/* Task Detail Side Panel */}
        {selectedTaskId && (
          <TaskDetailSidePanel
            taskId={selectedTaskId}
            isOpen={isSidePanelOpen}
            onClose={handleSidePanelClose}
          />
        )}
      </div>
    </ThemeProvider>
  );
};

export default AllTasks;
